import { compact } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import * as React from "react";
import { Descendant, Editor, NodeEntry, Range, createEditor } from "slate";
import { Editable, RenderElementProps, RenderLeafProps, Slate, useSlate, withReact } from "slate-react";

import { EMPTY_EDITOR_STATE } from "constants/rich-text";

import { PluginSpec, ReactPart } from "./plugins";
import { slatePlaceholderStyles } from "./styles";

interface IDefaultHandlers {
  onKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void;
  decorate(node: NodeEntry): Range[];
  onDOMBeforeInput(event: Event): void;
  renderElement(props: RenderElementProps): JSX.Element;
  renderLeaf(props: RenderLeafProps): JSX.Element;
}

const DEFAULT_HANDLERS: IDefaultHandlers = {
  onKeyDown(_: React.KeyboardEvent<HTMLDivElement>): void {},
  decorate(_: NodeEntry): Range[] {
    return [];
  },
  onDOMBeforeInput(_: Event) {},
  renderElement(props: RenderElementProps): JSX.Element {
    return <div {...props.attributes}>{props.children}</div>;
  },
  renderLeaf(props: RenderLeafProps): JSX.Element {
    return <span {...props.attributes}>{props.children}</span>;
  },
};

const augmentHandlers: (
  editor: Editor,
  defaultHandlers: IDefaultHandlers,
  reactParts: ReactPart[]
) => IDefaultHandlers = (editor, defaultHandlers, reactParts) => {
  const handlers = { ...defaultHandlers };

  reactParts.reverse().forEach(reactPart => {
    if (reactPart.onKeyDown) {
      const { onKeyDown } = handlers;

      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      handlers.onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (reactPart.onKeyDown !== undefined) {
          reactPart.onKeyDown(event, editor, onKeyDown);
        }
      };
    }

    if (reactPart.decorate) {
      const { decorate } = handlers;

      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      handlers.decorate = entry =>
        reactPart.decorate !== undefined ? reactPart.decorate(entry, editor, decorate) : [];
    }

    if (reactPart.onDOMBeforeInput) {
      const { onDOMBeforeInput } = handlers;

      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      handlers.onDOMBeforeInput = (event: Event) => {
        if (reactPart.onDOMBeforeInput !== undefined) {
          reactPart.onDOMBeforeInput(event, editor, onDOMBeforeInput);
        }
      };
    }

    if (reactPart.renderElement) {
      const { renderElement } = handlers;

      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      handlers.renderElement = (props: RenderElementProps) =>
        reactPart.renderElement !== undefined ? (
          reactPart.renderElement(props, editor, renderElement)
        ) : (
          <div {...props.attributes}>{props.children}</div>
        );
    }

    if (reactPart.renderLeaf) {
      const { renderLeaf } = handlers;

      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      handlers.renderLeaf = (props: RenderLeafProps) =>
        reactPart.renderLeaf !== undefined ? (
          reactPart.renderLeaf(props, editor, renderLeaf)
        ) : (
          <span {...props.attributes}>{props.children}</span>
        );
    }
  });

  return handlers;
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createPluggedEditor = (writePlugins: ((editor: Editor) => Editor)[]) => {
  let editor = createEditor();

  writePlugins.reverse().forEach(withPlugin => {
    editor = withPlugin(editor);
  });

  return editor;
};

const RichEditorPluginContext = React.createContext<{ plugins: PluginSpec[] }>({
  plugins: [],
});

export const usePlugins: () => PluginSpec[] = () => {
  const pluginsContext = useContext(RichEditorPluginContext);

  return pluginsContext.plugins;
};

type RestEditableProps = Exclude<keyof React.ComponentProps<typeof Editable>, keyof ReactPart>;
type PluggedEditableProps = Pick<React.ComponentProps<typeof Editable>, RestEditableProps> & {
  onKeyDown?: (event: React.KeyboardEvent, pluginKeyDown: (event: React.KeyboardEvent) => void) => void;
  placeholder?: string;
};

export const RichTextArea: React.FC<PluggedEditableProps> = props => {
  const editor = useSlate();
  const plugins = usePlugins();
  const { placeholder, disabled, readOnly, ...restProps } = props;
  const isEditorEmpty = JSON.stringify(EMPTY_EDITOR_STATE) === JSON.stringify(editor.children);

  const augmentedHandlers = useMemo<IDefaultHandlers>(
    () => augmentHandlers(editor, DEFAULT_HANDLERS, compact(plugins.map(plugin => plugin[1]))),
    [editor, plugins]
  );

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const pluginOnKeyDown = augmentedHandlers.onKeyDown;

    if (props.onKeyDown !== undefined) {
      // @ts-expect-error TS(2345): Argument of type '(e: KeyboardEvent<HTMLDivElement... Remove this comment to see the full error message
      props.onKeyDown(event, pluginOnKeyDown);
    } else {
      pluginOnKeyDown(event);
    }
  };

  return (
    <div className="notranslate">
      <Editable
        placeholder={isEditorEmpty ? placeholder : undefined}
        renderPlaceholder={({ attributes, children }) => (
          <span {...attributes} css={slatePlaceholderStyles}>
            {children}
          </span>
        )}
        translate="no"
        readOnly={readOnly || disabled}
        {...augmentedHandlers}
        {...restProps}
        onKeyDown={onKeyDown}
      />
    </div>
  );
};

interface IRichEditorProps {
  plugins: PluginSpec[];
  initialValue?: Descendant[];
  onChange?: (value: Descendant[]) => void;
  onInit?: (editor: Editor) => void;
  value?: Descendant[];
  children?: React.ReactNode;
}

const RichTextEditor: React.FC<IRichEditorProps> = ({ plugins, initialValue, value, onChange, onInit, children }) => {
  const editorPlugins = useMemo(() => {
    const userPlugins = plugins.map(plugin => plugin[0]);

    return userPlugins.includes(withReact) ? userPlugins : [withReact, ...userPlugins];
  }, [plugins]);

  const [internalEditorValue, setInternalEditorValue] = useState(
    initialValue || ([{ type: "paragraph", children: [{ text: "" }] }] as Descendant[])
  );
  const editorOnChange = useMemo(
    () =>
      onChange || // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      ((newValue: Descendant[]) => {
        setInternalEditorValue(newValue);
      }),
    [onChange]
  );

  const editorValue = value || internalEditorValue;

  const editor = useMemo(() => createPluggedEditor(editorPlugins), [editorPlugins]);
  useEffect(() => {
    if (onInit !== undefined) {
      onInit(editor);
    }
  }, [editor, onInit]);

  return (
    <Slate editor={editor} value={editorValue} onChange={editorOnChange}>
      <RichEditorPluginContext.Provider value={{ plugins }}>{children}</RichEditorPluginContext.Provider>
    </Slate>
  );
};

export default RichTextEditor;
